import React, { useState, useEffect } from "react";
import "@/App.css";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  Sparkles,
  Star,
  MapPin,
  ArrowRight,
  Heart,
  Users,
  BookOpen,
  GraduationCap,
  Target,
  Languages,
  Clock,
  Quote,
  ChevronDown,
  Phone,
  Mail,
  Send,
  Menu,
  X,
} from "lucide-react";

const SUBJECTS = [
  "Matemática",
  "Português",
  "Inglês",
  "Físico-Química",
  "Biologia",
  "História",
  "Francês",
  "Geografia",
  "Filosofia",
  "Economia",
  "Estudo do Meio",
  "Geometria",
];

const SERVICES = [
  {
    icon: BookOpen,
    tag: "1.º ao 9.º ano",
    title: "Apoio ao Estudo",
    desc: "Acompanhamento diário dos TPC, organização do estudo e revisão de matérias.",
    bg: "bg-butter",
    span: "md:col-span-7",
  },
  {
    icon: GraduationCap,
    tag: "Todos os ciclos",
    title: "Explicações",
    desc: "Individuais ou em pequenos grupos, focadas nas dificuldades de cada aluno.",
    bg: "bg-mint",
    span: "md:col-span-5",
  },
  {
    icon: Target,
    tag: "9.º · 11.º · 12.º",
    title: "Preparação para Exames",
    desc: "Provas de aferição, exames nacionais e testes intermédios — preparação dedicada.",
    bg: "bg-lavender",
    span: "md:col-span-7",
  },
  {
    icon: Languages,
    tag: "Iniciante a avançado",
    title: "Línguas",
    desc: "Inglês e Francês com práticas de oralidade, gramática e compreensão escrita.",
    bg: "bg-white border border-black/5",
    span: "md:col-span-5",
  },
  {
    icon: Users,
    tag: "Máx. 4 alunos",
    title: "Aulas de Grupo",
    desc: "Pequenos grupos (até 4 alunos) para estudar em conjunto com bons resultados.",
    bg: "bg-terracotta text-white",
    span: "md:col-span-12",
    invert: true,
  },
];

const SCHEDULE = [
  { day: "Segunda-feira", hours: "13:00 – 20:00" },
  { day: "Terça-feira", hours: "13:00 – 20:00" },
  { day: "Quarta-feira", hours: "13:00 – 20:00", today: true },
  { day: "Quinta-feira", hours: "13:00 – 20:00" },
  { day: "Sexta-feira", hours: "13:00 – 20:00" },
  { day: "Sábado", hours: "Fechado", closed: true },
  { day: "Domingo", hours: "Fechado", closed: true },
];

const TESTIMONIALS = [
  {
    quote:
      "A Beatriz adora ir ao centro. Notamos uma melhoria enorme na concentração e nas notas — e regressa sempre feliz a casa.",
    name: "Mariana S.",
    role: "Mãe da Beatriz, 4.º ano",
    bg: "bg-butter",
  },
  {
    quote:
      "Para mim, fez toda a diferença em Físico-Química. As explicações são claras e ajudaram-me a recuperar a confiança.",
    name: "João P.",
    role: "Aluno do 11.º ano",
    bg: "bg-mint",
  },
  {
    quote:
      "Equipa fantástica, super atenciosa. O Tomás ganhou método de estudo e melhorou a Matemática em poucos meses.",
    name: "Carla M.",
    role: "Mãe do Tomás, 7.º ano",
    bg: "bg-lavender",
  },
  {
    quote:
      "O ambiente é mesmo acolhedor. Sinto que posso pedir ajuda em tudo sem vergonha — e isso muda tudo.",
    name: "Sofia R.",
    role: "Aluna do 9.º ano",
    bg: "bg-white border border-black/5",
  },
];

const FAQS = [
  {
    q: "Quais os anos de escolaridade que acompanham?",
    a: "Trabalhamos com alunos do 1.º ciclo ao secundário — do 1.º ao 12.º ano — adaptando o acompanhamento à fase escolar de cada aluno.",
  },
  {
    q: "Como funcionam as explicações?",
    a: "As explicações podem ser individuais ou em pequenos grupos (até 4 alunos). Começamos por uma avaliação inicial e definimos um plano de estudo personalizado.",
  },
  {
    q: "Qual é o preçário?",
    a: "Os preços variam consoante o ano de escolaridade, o número de aulas semanais e o formato (individual ou grupo). Contacta-nos para uma proposta à medida.",
  },
  {
    q: "É possível marcar uma visita?",
    a: "Claro! Podes marcar uma visita ao centro através do formulário, telefone ou WhatsApp. Mostramos-te o espaço e respondemos a todas as dúvidas.",
  },
  {
    q: "Preparam alunos para exames nacionais?",
    a: "Sim. Temos um programa dedicado à preparação para provas de aferição, exames nacionais e testes intermédios, com revisões intensivas e simulações.",
  },
  {
    q: "Onde está localizado o centro?",
    a: "Estamos em Lisboa, na R. Prof. Delfim Santos 7C, 1600-610 Lisboa, perto de transportes públicos.",
  },
];

const NAV_LINKS = [
  { href: "#sobre", label: "Sobre" },
  { href: "#servicos", label: "Serviços" },
  { href: "#horario", label: "Horário" },
  { href: "#faq", label: "FAQ" },
  { href: "#contactos", label: "Contactos" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "bg-cream/85 backdrop-blur-md border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <a
          href="#inicio"
          data-testid="logo-link"
          className="flex items-center gap-2"
        >
          <div className="h-10 w-10 rounded-2xl bg-terracotta grid place-items-center text-white shadow-soft">
            <Sparkles size={20} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-xl tracking-tight">
              CRIARTE
            </div>
            <div className="text-[10px] uppercase tracking-widest text-charcoal/60 font-semibold">
              Centro de Estudos · Lisboa
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm font-semibold text-charcoal/80 hover:text-terracotta transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#contactos"
          data-testid="header-cta"
          className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-charcoal text-cream text-sm font-semibold hover:bg-terracotta transition-colors"
        >
          Marcar visita
          <ArrowRight size={14} />
        </a>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden h-10 w-10 grid place-items-center rounded-full bg-white shadow-soft"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-cream border-t border-black/5"
        >
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-semibold text-charcoal"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contactos"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-charcoal text-cream font-semibold"
            >
              Marcar visita <ArrowRight size={14} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
    >
      <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-butter blur-3xl opacity-60" />
      <div className="absolute top-40 -right-24 h-80 w-80 rounded-full bg-mint blur-3xl opacity-60" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-lavender blur-3xl opacity-50" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-black/5 shadow-soft mb-6">
              <Sparkles size={14} className="text-terracotta" />
              <span className="text-xs font-semibold tracking-wide uppercase">
                Centro de Estudos · Lisboa
              </span>
            </div>

            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Onde o estudo{" "}
              <span className="relative inline-block">
                <span className="relative z-10">encontra</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-butter -z-0" />
              </span>{" "}
              a <span className="text-terracotta italic">criatividade</span>.
            </h1>

            <p className="mt-6 text-lg md:text-xl text-charcoal/70 max-w-2xl leading-relaxed">
              Na CRIARTE acompanhamos alunos do 1.º ciclo ao secundário com
              explicações personalizadas e apoio ao estudo. Aprender pode — e
              deve — ser uma experiência inspiradora.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#contactos"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-600 hover:scale-[1.02] transition-all shadow-soft-lg"
              >
                Marcar uma visita
                <ArrowRight size={18} />
              </a>
              <a
                href="#servicos"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white border border-black/10 font-semibold hover:bg-black/5 transition-colors"
              >
                Ver serviços
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2" data-testid="hero-rating">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="fill-terracotta text-terracotta"
                    />
                  ))}
                </div>
                <span className="font-semibold">5.0</span>
                <span className="text-charcoal/60">no Google</span>
              </div>
              <div className="h-4 w-px bg-black/10" />
              <div className="flex items-center gap-2 text-charcoal/70">
                <MapPin size={16} className="text-terracotta" />
                <span>R. Prof. Delfim Santos, Lisboa</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="relative rounded-[2rem] overflow-hidden shadow-soft-lg aspect-[4/5] bg-mint">
                <img
                  alt="Materiais de estudo e criatividade"
                  className="w-full h-full object-cover"
                  src="https://static.prod-images.emergentagent.com/jobs/27628ffe-107f-4d07-93cc-bface963dc23/images/58b44bf34c96810fd7dd24470c55d61b950541d0e206b946e27b2b658b3a567d.png"
                />
              </div>
              <div
                data-testid="hero-floating-badge-1"
                className="absolute -left-6 top-10 bg-white rounded-2xl shadow-soft-lg px-5 py-4 float-slow"
              >
                <div className="text-xs text-charcoal/60 font-medium">
                  Anos a ensinar
                </div>
                <div className="font-display font-bold text-3xl">10+</div>
              </div>
              <div
                data-testid="hero-floating-badge-2"
                className="absolute -right-4 bottom-10 bg-butter rounded-2xl shadow-soft-lg px-5 py-4 float-slower"
              >
                <div className="text-xs font-semibold uppercase tracking-wide text-charcoal/70">
                  Turmas reduzidas
                </div>
                <div className="font-display font-bold text-2xl">Foco em ti</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SubjectsMarquee() {
  return (
    <section
      data-testid="subjects-marquee"
      className="py-10 border-y border-black/5 bg-white"
    >
      <div className="marquee-mask">
        <Marquee speed={50} gradient={false}>
          {SUBJECTS.map((s) => (
            <div
              key={s}
              data-testid={`subject-${s.toLowerCase().replace(/[^\w]+/g, "-")}`}
              className="flex items-center gap-6 mx-6"
            >
              <span className="font-display font-bold text-3xl md:text-5xl tracking-tight text-charcoal/80">
                {s}
              </span>
              <span className="h-3 w-3 rounded-full bg-terracotta" />
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="sobre" data-testid="about-section" className="py-24 md:py-32">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden aspect-[4/5] shadow-soft-lg">
                <img
                  alt="Crianças a aprender e a criar"
                  className="w-full h-full object-cover"
                  src="https://images.pexels.com/photos/5063475/pexels-photo-5063475.jpeg"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-terracotta text-white px-6 py-5 rounded-2xl shadow-soft-lg max-w-[200px]">
                <div className="font-display font-bold text-2xl leading-tight">
                  Aprender com sentido.
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
              Sobre nós
            </span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
              Um centro de estudos que acredita no potencial de cada aluno.
            </h2>
            <p className="mt-6 text-lg text-charcoal/70 leading-relaxed">
              A CRIARTE nasceu da vontade de oferecer mais do que explicações:
              criar um espaço onde crianças e jovens encontram apoio, confiança
              e ferramentas para conquistar bons resultados — sem perder o gosto
              pela descoberta. Combinamos rigor pedagógico com um toque criativo
              que torna cada aula numa pequena viagem.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                {
                  icon: Heart,
                  title: "Atenção individual",
                  desc: "Cada aluno é único. Ajustamos o ritmo, os exercícios e a abordagem.",
                  bg: "bg-butter",
                },
                {
                  icon: Users,
                  title: "Ambiente acolhedor",
                  desc: "Um espaço onde estudar deixa de ser obrigação para ser prazer.",
                  bg: "bg-mint",
                },
                {
                  icon: Sparkles,
                  title: "Aprender com arte",
                  desc: "Integramos atividades criativas para tornar a matéria mais memorável.",
                  bg: "bg-lavender",
                },
              ].map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`about-pillar-${i}`}
                    className={`${p.bg} rounded-2xl p-5 shadow-soft`}
                  >
                    <Icon size={22} className="text-charcoal mb-3" />
                    <h3 className="font-display font-bold text-lg mb-1">
                      {p.title}
                    </h3>
                    <p className="text-sm text-charcoal/70 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container-x">
        <div className="max-w-3xl mb-14">
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
            O que fazemos
          </span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
            Serviços pensados para cada fase do percurso escolar.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`${s.bg} ${s.span} rounded-3xl p-7 md:p-9 shadow-soft transition-shadow hover:shadow-soft-lg flex flex-col`}
              >
                <div className="flex items-start justify-between mb-6">
                  <Icon
                    size={32}
                    className={s.invert ? "text-white" : "text-charcoal"}
                  />
                  <span
                    className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${
                      s.invert
                        ? "bg-white/20 text-white"
                        : "bg-charcoal/5 text-charcoal/70"
                    }`}
                  >
                    {s.tag}
                  </span>
                </div>
                <h3
                  className={`font-display font-bold text-2xl md:text-3xl mb-3 ${
                    s.invert ? "text-white" : ""
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`leading-relaxed ${
                    s.invert ? "text-white/85" : "text-charcoal/70"
                  }`}
                >
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Schedule() {
  return (
    <section
      id="horario"
      data-testid="schedule-section"
      className="py-24 md:py-32"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
              Horário
            </span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
              Estamos cá para ti, todos os dias úteis.
            </h2>
            <p className="mt-6 text-lg text-charcoal/70 leading-relaxed">
              Atendimento de segunda a sexta-feira. Para aulas fora deste
              horário, fala connosco — encontramos a solução certa.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-mint">
              <Clock size={18} />
              <span className="font-semibold">Aberto: 13h – 20h</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-soft border border-black/5">
              <ul className="divide-y divide-black/5">
                {SCHEDULE.map((row, i) => (
                  <li
                    key={row.day}
                    data-testid={`schedule-row-${i}`}
                    className={`flex items-center justify-between py-4 px-2 rounded-xl ${
                      row.today ? "bg-butter/40" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`h-2 w-2 rounded-full ${
                          row.closed ? "bg-charcoal/20" : "bg-terracotta"
                        }`}
                      />
                      <span className="font-display font-semibold text-lg">
                        {row.day}
                      </span>
                      {row.today && (
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-terracotta text-white">
                          Hoje
                        </span>
                      )}
                    </div>
                    <span
                      className={`font-medium ${
                        row.closed ? "text-charcoal/40" : "text-charcoal"
                      }`}
                    >
                      {row.hours}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section
      id="testemunhos"
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
              Quem nos visita, fica
            </span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
              Histórias de quem cresceu connosco.
            </h2>
          </div>
          <div className="flex items-center gap-2 bg-cream px-5 py-3 rounded-full shadow-soft">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-terracotta text-terracotta"
                />
              ))}
            </div>
            <span className="font-bold">5.0</span>
            <span className="text-sm text-charcoal/60">avaliações Google</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`${t.bg} rounded-3xl p-8 shadow-soft relative`}
            >
              <Quote
                size={48}
                className="absolute top-6 right-6 text-charcoal/10"
              />
              <p className="text-lg leading-relaxed mb-6 relative z-10">
                “{t.quote}”
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-sm text-charcoal/60">{t.role}</div>
                </div>
                <div className="flex">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      size={14}
                      className="fill-terracotta text-terracotta"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a, index, open, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="bg-white rounded-2xl border border-black/5 shadow-soft px-6"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between font-display font-semibold text-lg text-left py-5"
      >
        {q}
        <ChevronDown
          size={18}
          className={`shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="pb-6 text-charcoal/70 leading-relaxed">{a}</div>
      )}
    </div>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
              Perguntas frequentes
            </span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-tight">
              Tens dúvidas? Nós respondemos.
            </h2>
            <p className="mt-6 text-charcoal/70">
              Se não encontrares aqui a tua resposta, contacta-nos diretamente.
              Respondemos rapidamente.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="space-y-3">
              {FAQS.map((f, i) => (
                <FAQItem
                  key={f.q}
                  q={f.q}
                  a={f.a}
                  index={i}
                  open={openIndex === i}
                  onToggle={() =>
                    setOpenIndex((cur) => (cur === i ? null : i))
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    e.target.reset();
  };

  return (
    <section
      id="contactos"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-charcoal text-cream relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-terracotta/30 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-butter/10 blur-3xl" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-widest text-terracotta mb-4">
              Contactos
            </span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
              Vamos conversar sobre o futuro do teu filho.
            </h2>
            <p className="mt-6 text-cream/70 leading-relaxed">
              Marca uma visita ou envia-nos uma mensagem. Respondemos em menos
              de 24 horas, com uma proposta personalizada.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="tel:+351917608330"
                data-testid="contact-phone-link"
                className="flex items-start gap-4 group"
              >
                <div className="h-11 w-11 rounded-full bg-terracotta grid place-items-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-cream/50">
                    Telefone
                  </div>
                  <div className="font-display text-lg font-semibold">
                    +351 917 608 330
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full bg-mint text-charcoal grid place-items-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-cream/50">
                    Morada
                  </div>
                  <div className="font-display text-lg font-semibold leading-tight">
                    R. Prof. Delfim Santos 7C
                    <br />
                    1600-610 Lisboa
                  </div>
                  <a
                    href="https://maps.app.goo.gl/ZkJkpReJsuEdbe6H7"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-maps-link"
                    className="text-sm text-terracotta hover:underline mt-1 inline-block"
                  >
                    Ver no Google Maps →
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full bg-butter text-charcoal grid place-items-center flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-cream/50">
                    Horário
                  </div>
                  <div className="font-display text-lg font-semibold">
                    Seg – Sex · 13h às 20h
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-cream text-charcoal rounded-3xl p-7 md:p-10 shadow-soft-lg"
            >
              <h3 className="font-display font-bold text-2xl mb-6">
                Pedido de informação
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold mb-1.5 block">
                    Nome *
                  </label>
                  <input
                    required
                    data-testid="contact-input-name"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-black/10 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                    placeholder="O teu nome"
                    name="name"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-1.5 block">
                    Email *
                  </label>
                  <input
                    required
                    type="email"
                    data-testid="contact-input-email"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-black/10 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                    placeholder="email@exemplo.com"
                    name="email"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-1.5 block">
                    Telefone
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-black/10 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                    placeholder="+351 ..."
                    name="phone"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-1.5 block">
                    Interesse
                  </label>
                  <select
                    name="subject_interest"
                    data-testid="contact-select-subject"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-black/10 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                  >
                    <option value="">Seleciona...</option>
                    <option value="Apoio ao Estudo">Apoio ao Estudo</option>
                    <option value="Explicações de Matemática">
                      Explicações de Matemática
                    </option>
                    <option value="Explicações de Português">
                      Explicações de Português
                    </option>
                    <option value="Explicações de Inglês">
                      Explicações de Inglês
                    </option>
                    <option value="Físico-Química">Físico-Química</option>
                    <option value="Preparação para Exames">
                      Preparação para Exames
                    </option>
                    <option value="Aulas de Grupo">Aulas de Grupo</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-semibold mb-1.5 block">
                  Mensagem *
                </label>
                <textarea
                  required
                  rows="5"
                  name="message"
                  data-testid="contact-input-message"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-black/10 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none resize-none transition"
                  placeholder="Conta-nos um pouco sobre o aluno e o que procuras..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-600 hover:scale-[1.02] transition-all shadow-soft"
              >
                {submitted ? "Mensagem enviada ✓" : "Enviar mensagem"}
                {!submitted && <Send size={18} />}
              </button>
              {submitted && (
                <p
                  data-testid="contact-success"
                  className="mt-4 text-sm text-terracotta font-semibold"
                >
                  Obrigado! Responderemos em menos de 24 horas.
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-charcoal text-cream/70 border-t border-white/5"
    >
      <div className="container-x py-12 grid md:grid-cols-3 gap-8 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-2xl bg-terracotta grid place-items-center text-white">
              <Sparkles size={16} />
            </div>
            <div className="font-display font-extrabold text-xl text-cream">
              CRIARTE
            </div>
          </div>
          <p>Centro de Estudos · Lisboa</p>
          <p className="mt-1">Onde o estudo encontra a criatividade.</p>
        </div>
        <div>
          <div className="text-cream font-semibold mb-3">Navegar</div>
          <ul className="space-y-2">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="hover:text-terracotta transition">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-cream font-semibold mb-3">Contactos</div>
          <p>R. Prof. Delfim Santos 7C</p>
          <p>1600-610 Lisboa</p>
          <p className="mt-2">+351 917 608 330</p>
          <p>Seg – Sex · 13h às 20h</p>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="container-x py-5 text-xs text-cream/50 flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} CRIARTE. Todos os direitos reservados.</span>
          <span>Feito com cuidado em Lisboa.</span>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppFloat() {
  return (
    <a
      href="https://wa.me/351917608330?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20os%20servi%C3%A7os%20da%20CRIARTE."
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-floating-button"
      aria-label="Falar no WhatsApp"
      className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#25D366] text-white grid place-items-center shadow-soft-lg hover:scale-110 transition-transform"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
        <path d="M20.52 3.48A11.9 11.9 0 0 0 12.04 0C5.49 0 .17 5.32.17 11.87c0 2.09.55 4.13 1.6 5.94L0 24l6.34-1.66a11.86 11.86 0 0 0 5.7 1.45h.01c6.55 0 11.87-5.32 11.87-11.87 0-3.17-1.23-6.15-3.4-8.44ZM12.05 21.5h-.01a9.6 9.6 0 0 1-4.9-1.34l-.35-.21-3.76.99 1-3.66-.23-.38a9.6 9.6 0 0 1-1.48-5.13c0-5.31 4.32-9.63 9.64-9.63 2.57 0 4.99 1 6.81 2.82a9.56 9.56 0 0 1 2.83 6.81c0 5.31-4.32 9.63-9.55 9.73Zm5.27-7.18c-.29-.14-1.71-.84-1.97-.94-.27-.1-.46-.14-.66.14-.2.29-.76.94-.94 1.14-.17.19-.34.21-.63.07-.29-.14-1.22-.45-2.32-1.44-.86-.77-1.43-1.71-1.6-2-.17-.29-.02-.45.13-.59.13-.13.29-.34.43-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.66-1.58-.9-2.17-.24-.57-.48-.49-.66-.5l-.56-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.39 0 1.41 1.02 2.77 1.17 2.97.14.19 2.02 3.08 4.89 4.32.68.29 1.21.47 1.63.6.68.22 1.31.19 1.8.12.55-.08 1.71-.7 1.95-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34Z" />
      </svg>
    </a>
  );
}

function App() {
  return (
    <div className="App">
      <div
        className="min-h-screen bg-cream text-charcoal"
        data-testid="home-page"
      >
        <Header />
        <main>
          <Hero />
          <SubjectsMarquee />
          <About />
          <Services />
          <Schedule />
          <Testimonials />
          <FAQ />
          <Contact />
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </div>
  );
}

export default App;
